import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES } from "../../data/holidays";
import { Chapter, Reveal } from "./shared";

export default function HolidayRiding() {
  const { t } = useTranslation();
  const R = HOLIDAY_IMAGES.riding;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [40, -60]);
  const yB = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <section ref={ref} className="bg-bone-deep py-24 lg:py-40 overflow-hidden" data-testid="holidays-riding">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <Chapter number={t("holidays.riding.chapter")} label={t("holidays.riding.eyebrow")} />
            <Reveal delay={0.1}>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl text-forest leading-[0.98] mt-8 uppercase tracking-tight">
                {t("holidays.riding.title").split(". ").map((part, i) => (
                  <span key={i} className={`block ${i === 1 ? "italic normal-case pl-[6%]" : ""}`}>{part.replace(/\.$/, "")}.</span>
                ))}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p>{t("holidays.riding.p1")}</p>
            <p>{t("holidays.riding.p2")}</p>
            <p>{t("holidays.riding.p3")}</p>
          </Reveal>
        </div>

        {/* Editorial photo composition */}
        <div className="mt-16 lg:mt-24 grid grid-cols-12 gap-3 md:gap-5 items-start">
          <Photo className="col-span-12 md:col-span-7 aspect-[16/10]" src={R.berm} alt={t("holidays.riding.alts.berm")} />
          <motion.div style={{ y: yA }} className="col-span-6 md:col-span-5 md:pt-16">
            <Photo className="aspect-[4/5]" src={R.climb} alt={t("holidays.riding.alts.climb")} />
          </motion.div>
          <motion.div style={{ y: yB }} className="col-span-6 md:col-span-4 md:-mt-24">
            <Photo className="aspect-[4/5]" src={R.viewpoint} alt={t("holidays.riding.alts.viewpoint")} />
          </motion.div>
          <Photo className="col-span-12 md:col-span-8 aspect-[21/9] md:mt-8" src={R.panorama} alt={t("holidays.riding.alts.panorama")} />
          <Photo className="col-span-6 md:col-span-5 aspect-[4/3]" src={R.group} alt={t("holidays.riding.alts.group")} />
          <Reveal className="col-span-6 md:col-span-7 self-center px-2 md:px-10">
            <p className="font-display italic text-2xl sm:text-3xl lg:text-5xl text-forest leading-tight" data-testid="riding-quote">
              {t("holidays.riding.quote_a")}<br />
              <span className="not-italic text-terracotta">{t("holidays.riding.quote_b")}</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Photo({ src, alt, className = "" }) {
  return (
    <Reveal className={`relative overflow-hidden bg-stoneWarm group ${className}`} y={30}>
      <img src={src} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
    </Reveal>
  );
}
